// Controles de formulario traídos del prototipo (validados en las rondas del piloto).
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { nf } from '../logica/dinero';
import { PAIS, PAISES_LISTA, PRINCIPAL, buscarPais, paisLabel, type Pais } from '../logica/paises';
import { parseDec, sinTildes } from '../logica/texto';

// Solo un movimiento real del mouse cambia la opción activa de una lista. El navegador simula "pasar el mouse" cuando
// la lista se desplaza con el teclado bajo un puntero quieto, y eso movía la selección sin que nadie tocara el mouse.
function useMouseReal() {
  const ultimo = useRef({ x: -1, y: -1 });
  return (e: MouseEvent, hacer: () => void) => {
    if (e.clientX === ultimo.current.x && e.clientY === ultimo.current.y) return;
    ultimo.current = { x: e.clientX, y: e.clientY };
    hacer();
  };
}

export const Pill = ({ tono, children }: { tono?: string; children: ComponentChildren }) => <span class={'pill ' + (tono || '')}>{children}</span>;

type Opcion<T> = T | { id: T; label: string };
export function Chips<T extends string | number>({ opciones, valor, onValor, conteos, etiqueta }: { opciones: Opcion<T>[]; valor: T | '' | null; onValor: (v: T) => void; conteos?: Record<string, number>; etiqueta?: string }) {
  return (
    <div class="chips" role="group" aria-label={etiqueta}>
      {opciones.map(o => {
        const id = (typeof o === 'object' ? o.id : o) as T, lb = typeof o === 'object' ? o.label : String(o);
        return (
          <button key={String(id)} type="button" class={'chip' + (valor === id ? ' on' : '')} aria-pressed={valor === id} onClick={() => onValor(id)}>
            {lb}{conteos && conteos[String(id)] != null && <span class="c">{conteos[String(id)]}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Montos sin decimales con punto de miles mientras se escribe ("165.000").
export function MoneyInput({ value, onValue, id, err, placeholder, label }: { value: number; onValue: (v: number) => void; id?: string; err?: boolean; placeholder?: string; label?: string }) {
  return <input id={id} class={'in r' + (err ? ' err' : '')} type="text" inputmode="numeric" placeholder={placeholder || '0'} aria-label={label}
    value={value ? nf(value) : ''} onInput={e => { const d = e.currentTarget.value.replace(/\D/g, ''); onValue(d === '' ? 0 : +d); }} />;
}

// Trae al campo un valor nuevo que llega de afuera (otro guardado), sin borrar lo que se está escribiendo al abrirlo:
// solo actúa si el valor cambió desde la última vez, no al montar el campo.
function useSincronizar<T>(valor: T, aplicar: () => void) {
  const antes = useRef(valor);
  useEffect(() => { if (antes.current !== valor) { antes.current = valor; aplicar(); } }, [valor]);
}

// Campo que se guarda al salir de él (sin botón "Guardar"), con su formato y su validación. Enter también guarda.
// Si no se pudo guardar, el texto escrito se queda en el campo con el aviso.
export function CampoGuardado({ id, label, ariaLabel, valor, formato, validar, placeholder, area, onGuardar }: {
  id: string; label?: string; ariaLabel?: string; valor: string; formato?: (s: string) => string; validar?: (s: string) => string; placeholder?: string; area?: boolean; onGuardar: (v: string) => Promise<void>;
}) {
  const [v, setV] = useState(valor), [err, setErr] = useState('');
  useSincronizar(valor, () => setV(valor));
  const guardar = () => {
    const f = formato ? formato(v) : v.trim(), e = validar ? validar(f) : '';
    setErr(e);
    if (e) return;
    setV(f);
    if (f !== valor) onGuardar(f).catch(x => setErr('No se pudo guardar: ' + ((x as Error).message || x)));
  };
  const props = { id, class: 'in' + (err ? ' err' : ''), value: v, placeholder, 'aria-label': label ? undefined : ariaLabel, 'aria-invalid': err ? true : undefined,
    onInput: (e: Event) => setV((e.currentTarget as HTMLInputElement).value), onBlur: guardar };
  return (
    <div class="field">{label && <label for={id}>{label}</label>}
      {area ? <textarea {...props} rows={3} /> : <input {...props} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />}
      {err && <span class="errtxt">{err}</span>}</div>
  );
}

// Número entero que se guarda al salir del campo (tarifas, tasas, días, porcentajes), con punto de miles.
export function NumeroGuardado({ id, label, valor, min, max, onGuardar }: { id: string; label: string; valor: number; min: number; max?: number; onGuardar: (v: number) => Promise<void> }) {
  const txt = (n: number) => (Number.isFinite(n) ? nf(n) : ''), [v, setV] = useState(txt(valor)), [err, setErr] = useState('');
  useSincronizar(valor, () => setV(txt(valor)));
  const guardar = () => {
    const d = v.replace(/\D/g, ''), n = +d;
    if (!d || n < min || (max != null && n > max)) { setErr(max != null ? `Escribe un número entre ${nf(min)} y ${nf(max)}.` : `Escribe un número desde ${nf(min)}.`); return; }
    setErr('');
    if (n !== valor) onGuardar(n).catch(x => setErr('No se pudo guardar: ' + ((x as Error).message || x)));
  };
  return (
    <div class="field"><label for={id}>{label}</label>
      <input id={id} class={'in r' + (err ? ' err' : '')} type="text" inputmode="numeric" value={v} aria-invalid={err ? true : undefined}
        onInput={e => { const d = e.currentTarget.value.replace(/\D/g, ''); setV(d ? nf(+d) : ''); }} onBlur={guardar} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      {err && <span class="errtxt">{err}</span>}</div>
  );
}

// Valor en pesos que puede quedar vacío (sin registrar) o en cero (no costó), con punto de miles mientras se escribe.
export function ValorLibre({ id, label, valor, onValor, placeholder, err }: { id: string; label: string; valor: number | null; onValor: (v: number | null) => void; placeholder?: string; err?: boolean }) {
  return <input id={id} class={'in r' + (err ? ' err' : '')} type="text" inputmode="numeric" aria-label={label} placeholder={placeholder || 'Sin registrar'} value={valor == null ? '' : nf(valor)}
    onInput={e => { const d = e.currentTarget.value.replace(/\D/g, ''); onValor(d === '' ? null : +d); }} />;
}

// Cantidad con decimales que se guarda al salir del campo (fórmula de una prenda: 0,35 metros). Vacío = sin definir.
export function DecimalGuardado({ id, ariaLabel, valor, decimales = 4, onGuardar }: { id: string; ariaLabel: string; valor: number | null; decimales?: number; onGuardar: (v: number | null) => Promise<void> }) {
  const txt = (n: number | null) => (n == null ? '' : nf(n, decimales)), [v, setV] = useState(txt(valor)), [err, setErr] = useState('');
  useSincronizar(valor, () => setV(txt(valor)));
  const guardar = () => {
    const n = parseDec(v);
    if (v.trim() && (n == null || n < 0)) { setErr('Escribe un número, por ejemplo 0,35.'); return; }
    setErr('');
    if (n !== valor) onGuardar(n).catch(x => setErr('No se pudo guardar: ' + ((x as Error).message || x)));
  };
  return (
    <div class="field">
      <input id={id} class={'in r' + (err || valor == null ? ' err' : '')} type="text" inputmode="decimal" aria-label={ariaLabel} value={v} placeholder="Falta" aria-invalid={err ? true : undefined}
        onInput={e => setV(e.currentTarget.value)} onBlur={guardar} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      {err && <span class="errtxt">{err}</span>}</div>
  );
}

// Valor entero con punto de miles que se guarda al salir del campo; vacío = sin valor (un precio que no se ofrece).
export function ValorGuardado({ id, ariaLabel, valor, placeholder, marcar, onGuardar }: { id: string; ariaLabel: string; valor: number | null; placeholder?: string; marcar?: boolean; onGuardar: (v: number | null) => Promise<void> }) {
  const txt = (n: number | null) => (n ? nf(n) : ''), [v, setV] = useState(txt(valor)), [err, setErr] = useState('');
  useSincronizar(valor, () => setV(txt(valor)));
  const guardar = () => {
    const d = v.replace(/\D/g, ''), n = d ? +d : null;
    setErr('');
    if ((n || null) !== (valor || null)) onGuardar(n).catch(x => setErr('No se pudo guardar: ' + ((x as Error).message || x)));
  };
  return (
    <span class="valor-g">
      <input id={id} class={'in r' + (err || marcar ? ' err' : '')} type="text" inputmode="numeric" aria-label={ariaLabel} value={v} placeholder={placeholder || '—'}
        onInput={e => { const d = e.currentTarget.value.replace(/\D/g, ''); setV(d ? nf(+d) : ''); }} onBlur={guardar} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      {err && <span class="errtxt">{err}</span>}</span>
  );
}

// Botón de dos toques para acciones que no se deshacen fácil.
export function ConfirmBtn({ label, armedLabel, onConfirm, cls }: { label: string; armedLabel: string; onConfirm: () => void; cls?: string }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => { if (!armed) return; const t = setTimeout(() => setArmed(false), 4000); return () => clearTimeout(t); }, [armed]);
  return <button type="button" class={'btn sm ' + (armed ? 'armed' : (cls || ''))} onClick={() => { if (armed) { setArmed(false); onConfirm(); } else setArmed(true); }}>{armed ? armedLabel : label}</button>;
}

// Selector de país (ronda 3 del piloto): al tocarlo despliega la lista completa (frecuentes primero) y al escribir
// filtra por nombre o por indicativo (+44). Lo escrito que no corresponde a un país vuelve al país anterior.
export function PaisSelect({ id, iso, onElegir, conDial, err, ariaLabel, placeholder }: { id: string; iso: string; onElegir: (p: Pais) => void; conDial?: boolean; err?: boolean; ariaLabel?: string; placeholder?: string }) {
  const sel = PAIS[iso] || null, etq = (p: Pais) => (conDial ? paisLabel(p) : p.nombre);
  const [q, setQ] = useState<string | null>(null), [abierto, setAbierto] = useState(false), [act, setAct] = useState(0);
  const ref = useRef<HTMLInputElement>(null), lista = useRef<HTMLUListElement>(null), mouse = useMouseReal();
  const t = sinTildes(q || '').trim(), porDial = /^\+?\s*\d+$/.test(t), dial = t.replace(/\D/g, '');
  const rango = (p: Pais) => (porDial ? (p.dial === dial ? (PRINCIPAL[dial] === p.iso ? 0 : 1) : 2) : (sinTildes(p.nombre).startsWith(t) ? 0 : 1));
  const ops = !t ? PAISES_LISTA : PAISES_LISTA.filter(p => (porDial ? p.dial.startsWith(dial) : sinTildes(p.nombre).includes(t)))
    .map((p, i) => [p, i] as const).sort((a, b) => rango(a[0]) - rango(b[0]) || a[1] - b[1]).map(x => x[0]);
  useEffect(() => { const li = abierto && lista.current ? lista.current.children[act] as HTMLElement : null; li?.scrollIntoView?.({ block: 'nearest' }); }, [act, abierto]);
  const elegir = (p?: Pais) => { setQ(null); setAbierto(false); setAct(0); if (p) onElegir(p); };
  // Al salir: si lo escrito es un país, queda elegido. La lista se cierra con un respiro para que el toque en una opción alcance a llegar (iPad).
  const salir = () => {
    if (q !== null && q.trim()) { const p = ops.length === 1 ? ops[0] : buscarPais(q); if (p) onElegir(p); }
    setQ(null); setTimeout(() => { if (document.activeElement !== ref.current) setAbierto(false); }, 180);
  };
  const tecla = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!abierto) setAbierto(true); else setAct(Math.min(act + 1, ops.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAct(Math.max(act - 1, 0)); }
    else if (e.key === 'Enter') { if (abierto && ops[act]) { e.preventDefault(); elegir(ops[act]); } }
    else if (e.key === 'Escape') { setQ(null); setAbierto(false); }
  };
  return (
    <div class="combo">
      <input id={id} ref={ref} class={'in' + (err ? ' err' : '')} role="combobox" aria-expanded={abierto} aria-controls={id + '-lista'} aria-autocomplete="list" aria-label={ariaLabel}
        aria-activedescendant={abierto && ops[act] ? id + '-o-' + ops[act].iso : undefined} autocomplete="off" autocapitalize="off" spellcheck={false}
        placeholder={placeholder || 'Toca o escribe para buscar'} value={q !== null ? q : sel ? etq(sel) : ''}
        onFocus={e => { setAbierto(true); setAct(Math.max(0, sel ? PAISES_LISTA.indexOf(sel) : 0)); e.currentTarget.select(); }} onClick={() => setAbierto(true)}
        onInput={e => { setQ(e.currentTarget.value); setAbierto(true); setAct(0); }} onBlur={salir} onKeyDown={tecla} />
      <button type="button" class="combo-btn" tabIndex={-1} aria-label={(abierto ? 'Cerrar' : 'Ver') + ' la lista de países'} onMouseDown={e => e.preventDefault()}
        onClick={() => { if (abierto) setAbierto(false); else ref.current?.focus(); }}>▾</button>
      {abierto && (
        <ul id={id + '-lista'} class="combo-lista" role="listbox" ref={lista} aria-label={ariaLabel || 'Países'}>
          {ops.length ? ops.map((p, i) => (
            <li key={p.iso} id={id + '-o-' + p.iso} role="option" aria-selected={i === act} class={(i === act ? 'act' : '') + (sel && sel.iso === p.iso ? ' sel' : '')}
              onMouseDown={e => e.preventDefault()} onMouseMove={e => mouse(e, () => setAct(i))} onClick={() => elegir(p)}>
              <span>{p.nombre}</span>{conDial && <span class="dial">+{p.dial}</span>}
            </li>
          )) : <li class="vacio" role="presentation">Ningún país coincide. Prueba con el indicativo, por ejemplo +44.</li>}
        </ul>
      )}
    </div>
  );
}

// Color de tela: lista desplegable con la bolita de cada color de la paleta (o solo los colores de la prenda).
// Funciona con mouse, teclado y toque. Un valor que no está en la lista (de datos viejos) se muestra tal cual.
export function ColorSelect({ id, valor, opciones, onElegir, err, ariaLabel }: { id: string; valor: string; opciones: { nombre: string; hex: string }[]; onElegir: (nombre: string) => void; err?: boolean; ariaLabel?: string }) {
  // La opción activa también va en una referencia: las teclas seguidas no deben depender de que la pantalla ya se haya actualizado.
  const [abierto, setAbierto] = useState(false), [act, setActEstado] = useState(0), actRef = useRef(0), abiertoRef = useRef(false);
  const setAct = (v: number) => { actRef.current = v; setActEstado(v); };
  const setAbiertoR = (v: boolean) => { abiertoRef.current = v; setAbierto(v); };
  const ref = useRef<HTMLButtonElement>(null), lista = useRef<HTMLUListElement>(null), mouse = useMouseReal();
  const ops = [{ nombre: '', hex: '' }, ...opciones];
  const sel = opciones.find(o => sinTildes(o.nombre) === sinTildes(valor));
  useEffect(() => { const li = abierto && lista.current ? lista.current.children[act] as HTMLElement : null; li?.scrollIntoView?.({ block: 'nearest' }); }, [act, abierto]);
  const abrir = () => { setAct(Math.max(0, ops.findIndex(o => o.nombre && o === sel))); setAbiertoR(true); };
  const elegir = (o: { nombre: string }) => { setAbiertoR(false); onElegir(o.nombre); ref.current?.focus(); };
  const tecla = (e: KeyboardEvent) => {
    const a = actRef.current, ab = abiertoRef.current;
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!ab) abrir(); else setAct(Math.min(a + 1, ops.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAct(Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (ab) elegir(ops[a]); else abrir(); }
    else if (e.key === 'Escape') setAbiertoR(false);
  };
  return (
    <div class="combo">
      <button id={id} ref={ref} type="button" class={'in color-btn' + (err ? ' err' : '')} role="combobox" aria-haspopup="listbox" aria-expanded={abierto} aria-controls={id + '-lista'} aria-label={ariaLabel}
        aria-activedescendant={abierto ? id + '-o-' + act : undefined}
        onClick={() => (abiertoRef.current ? setAbiertoR(false) : abrir())} onKeyDown={tecla} onBlur={() => setTimeout(() => { if (document.activeElement !== ref.current) setAbiertoR(false); }, 180)}>
        {sel ? <><i class="dot" style={{ background: sel.hex }} />{sel.nombre}</> : valor ? valor : <span class="muted">Elegir color…</span>}
        <span class="flecha" aria-hidden="true">▾</span>
      </button>
      {abierto && (
        <ul id={id + '-lista'} class="combo-lista" role="listbox" ref={lista} aria-label={ariaLabel || 'Colores'}>
          {ops.map((o, i) => (
            <li key={o.nombre || '-'} id={id + '-o-' + i} role="option" aria-selected={i === act} class={(i === act ? 'act' : '') + (o.nombre && o === sel ? ' sel' : '')}
              onMouseDown={e => e.preventDefault()} onMouseMove={e => mouse(e, () => setAct(i))} onClick={() => elegir(o)}>
              {o.nombre ? <span><i class="dot" style={{ background: o.hex }} />{o.nombre}</span> : <span class="muted">Sin definir</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
